export default class SubstanceBarHtml {
    /** @type {HTMLElement} */
    #parentElement;
    /** @type {SubstanceBar} */
    #rootElement;
    /** @type {{highlightAddButtonWhenEmpty: boolean, showAddButton:boolean}} */
    #displayOptions;
    /** @type {SubstanceBarItem}*/
    #elementWithFakeSelection;

    #callbacks = {
        /**
         * @callback SubstanceBarHtml.onAddSubstanceClickCallbackType
         */
        onAddSubstanceClick: () => {/* to be overridden */},
        /**
         * @callback SubstanceBar.onSizeChangedCallbackType
         * @param isMinimized
         */
        onSizeChanged: (isMinimized) => {/* to be overridden */}
    }

    /**
     * @param {HTMLElement} parentElement
     * @param {{highlightAddButtonWhenEmpty?: boolean, showAddButton?:boolean}} options
     */
    init(parentElement, options = {}) {
        this.#parentElement = parentElement;
        this.#displayOptions = SubstanceBarHtml.#withDefaultOptions(options);
    }

    show() {
        if (this.#rootElement)
            this.#rootElement.remove();
        this.#parentElement.insertAdjacentHTML('beforeend', SubstanceBarHtml.#htmlTemplate(this.#displayOptions));
        this.#rootElement = this.#parentElement.lastElementChild;
        this.#toggleHighLightAddSubstanceBtn(true);
        this.#addEventListeners();
    }

    hide() {
        if (this.#rootElement) {
            this.#rootElement.remove();
            this.#rootElement = null;
        }
    }

    /**
     * @param {SubstanceBarItem} substanceBarItem
     * @param {number} index
     */
    addSubstanceItem(substanceBarItem, index) {
        this.#rootElement.addSubstanceItem(substanceBarItem, index);
        this.#toggleHighLightAddSubstanceBtn(false)
    }

    /**
     * @param {string} substanceId
     * @param {string} name
     * @param {string} color
     */
    updateSubstanceItem(substanceId, name, color) {
        this.#rootElement.updateSubstanceItem(substanceId, name, color);
    }

    /**
     * @param {string} substanceId
     */
    deleteSubstanceItem(substanceId) {
        this.#rootElement.deleteSubstanceItem(substanceId);
        if (!this.#rootElement.querySelectorAll('substance-bar-item').length)
            this.#toggleHighLightAddSubstanceBtn(true)
    }

    removeAllSubstances() {
        if (!this.#rootElement)
            return;
        this.#rootElement.emptySubstanceList();
    }
    displayAllSubstancesNormal() {
        if (!this.#rootElement)
            return;
        this.#rootElement.deselectAll();
    }
    setVisible(isVisible) {
        this.#rootElement.classList.toggle('visually-hidden', !isVisible);
    }
    /**
     * @param {SubstanceBar.onSizeChangedCallbackType} cb
     */
    onSizeChanged(cb) {
        this.#callbacks.onSizeChanged = cb
    }

    /**
     * @param {SubstanceBar.onSubstanceSelectedCallbackType} cb
     */
    onSubstanceSelected(cb) {
        this.#rootElement.onSubstanceSelected(cb);
    }

    /**
     * @param {SubstanceBar.onSubstanceDeselectedCallbackType} cb
     */
    onSubstanceDeselected(cb) {
        this.#rootElement.onSubstanceDeselected(cb);
    }

    /**
     * @param {SubstanceBarHtml.onAddSubstanceClickCallbackType} callback
     */
    onAddSubstanceClick(callback) {
        this.#callbacks.onAddSubstanceClick = callback;
    }

    selectFirstItem(){
        this.#elementWithFakeSelection = this.#rootElement.querySelector('substance-bar-item');
        if (this.#elementWithFakeSelection)
            this.#rootElement._onSubstanceClick(this.#elementWithFakeSelection);
    }

    deselectFirstItem(){
        const el = this.#rootElement.querySelector('substance-bar-item');
        if (el && this.#elementWithFakeSelection === el && el.hasAttribute('selected'))
            this.#rootElement._onSubstanceClick(this.#elementWithFakeSelection);
    }

    /** @param {boolean} isCompareMode */
    enterCompareMode(isCompareMode){
        const items = this.#rootElement.querySelectorAll('substance-bar-item, bar-button');
        for (const item of items){
            item.classList.toggle("compare-mode", isCompareMode);
            const btn = this.#rootElement.querySelector("#add-substance-btn");
            if(btn)
                btn.classList.toggle('visually-hidden', isCompareMode);
        }
    }

    #toggleHighLightAddSubstanceBtn(isHighLighted){
        if (!this.#displayOptions.highlightAddButtonWhenEmpty)
            return
        const addButton = this.#rootElement.querySelector("#add-substance-btn");
        if (!addButton)
            return;
        // Firefox cannot use styles :host-context, so we should set class "highlighted" directly to the add substance button
        const element = addButton
            .shadowRoot.querySelector('.bar-button__add-substance-button');
        element.classList.toggle("highlighted", isHighLighted)
    }

    #addEventListeners() {
        const addButton = this.#rootElement.querySelector('#add-substance-btn');
        if (addButton)
            addButton
                .addEventListener('click', () => {
                    this.#callbacks.onAddSubstanceClick();
                });
        this.#rootElement.onSizeChanged((isMinimized) => {
            this.#callbacks.onSizeChanged(isMinimized);
        });
    }

    /**
     * @param {{highlightAddButtonWhenEmpty?: boolean, showAddButton?:boolean}} displayOptions
     *  @returns {string}
     */
    static #htmlTemplate(displayOptions) {
        const tourTipTemplate = displayOptions.showAddButton ? '<div slot="tourtip" class="substance-bar__tip-container"><style> @import "/ui/js/components/substance-bar/substanceBar.css?v=2"; </style></div>' : "";
        const addSubstanceTemplate = displayOptions.showAddButton ? '<bar-button id="add-substance-btn" icon="plus" text="Add analyte" title="Add a compound to select a peak" slot="button"></bar-button>' : "";
        return `
            <substance-bar>
                ${tourTipTemplate}
                ${addSubstanceTemplate}
            </substance-bar>`;
    }

    /** @returns {{highlightAddButtonWhenEmpty?: boolean, showAddButton?:boolean}} */
    static #withDefaultOptions(userOptions) {
        return Object.assign({
                highlightAddButtonWhenEmpty: false,
                showAddButton: true
            },
            userOptions);
    }
}